import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Be Fluent Language Center';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://befluentcenter.com').replace(/\/$/, '');
const DEFAULT_IMAGE = `${SITE_URL}/befluent-logo.png`;

const PRIVATE_PATHS = [
  '/login',
  '/register',
  '/verify-email',
  '/verify-email/sent',
  '/dashboard/student',
  '/dashboard/teacher',
  '/preferences',
  '/thank-you/inquiry-received',
];

const PAGE_SEO = {
  '/': {
    title: 'Be Fluent Language Center',
    description: 'Learn English, Arabic, and more with Be Fluent Language Center through flexible courses, placement tests, and professional language training.',
  },
  '/courses': {
    title: 'Language Courses',
    description: 'Explore Be Fluent language courses for students, professionals, and companies. Find the right level and start learning with expert teachers.',
  },
  '/corporate': {
    title: 'Corporate Language Training',
    description: 'Corporate language training programs for companies that want to improve communication, confidence, and professional language skills.',
  },
  '/placement-test': {
    title: 'Placement Test',
    description: 'Take the Be Fluent placement test to find your current language level and choose the right course for your goals.',
  },
  '/about': {
    title: 'About Us',
    description: 'Learn about Be Fluent Language Center, our teaching approach, and how we help learners build real communication skills.',
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Contact Be Fluent Language Center to ask about courses, placement tests, schedules, enrollment, and corporate training.',
  },
  '/enroll': {
    title: 'Enroll Now',
    description: 'Enroll at Be Fluent Language Center and start your language-learning journey with a course that matches your level and goals.',
  },
  '/thank-you/inquiry-received': {
    title: 'Inquiry Received',
    description: 'Your Be Fluent Language Center request has been received successfully.',
  },
};

function toText(value, fallback = '') {
  if (typeof value === 'string') return value.trim() || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => toText(item, ''))
      .filter(Boolean)
      .join(' ')
      .trim();
    return joined || fallback;
  }
  return fallback;
}

function titleFromSlug(slug) {
  return slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getSeoForPath(pathname) {
  if (PAGE_SEO[pathname]) return PAGE_SEO[pathname];

  if (pathname.startsWith('/courses/')) {
    const courseName = titleFromSlug(pathname.replace('/courses/', ''));

    return {
      title: `${courseName} Course`,
      description: `Learn more about the ${courseName} course at Be Fluent Language Center, including course details, levels, and enrollment options.`,
    };
  }

  if (pathname.startsWith('/legal/')) {
    const pageName = titleFromSlug(pathname.replace('/legal/', ''));

    return {
      title: pageName,
      description: `${pageName} information for Be Fluent Language Center.`,
    };
  }

  return PAGE_SEO['/'];
}

function buildUrl(pathname) {
  return `${SITE_URL}${pathname === '/' ? '/' : pathname}`;
}

export default function SEOManager() {
  const location = useLocation();
  const pathname = location.pathname || '/';
  const seo = useMemo(() => getSeoForPath(pathname), [pathname]);
  const canonicalUrl = buildUrl(pathname);
  const noIndex = PRIVATE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  const baseTitle = toText(seo?.title, SITE_NAME);
  const description = toText(seo?.description, PAGE_SEO['/'].description);
  const title = baseTitle === SITE_NAME ? SITE_NAME : `${baseTitle} | ${SITE_NAME}`;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    sameAs: [],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
  };
  const organizationSchemaJson = JSON.stringify(organizationSchema) || '{}';
  const websiteSchemaJson = JSON.stringify(websiteSchema) || '{}';
  const scriptTags = [
    { type: 'application/ld+json', innerHTML: organizationSchemaJson },
    { type: 'application/ld+json', innerHTML: websiteSchemaJson },
  ];

  return (
    <Helmet title={title} script={scriptTags}>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={DEFAULT_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
}
